import React from 'react';
import { HandsClappingIcon } from '@revfluence/fresh-icons/solid/esm';
import { FragmentType, graphql, useFragment } from '@frontend/gql/social';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import MetricChangeIndicator from '@frontend/applications/Social/components/metric-change-indicator';
import { DateType } from '@frontend/applications/Social/types';
import {
  HighlightCardStat,
  HighlightCardStatLabel,
} from '@frontend/applications/Social/components/highlight-card';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipItemValue,
  TooltipArrow,
  TooltipContent,
} from '@frontend/applications/Social/components/tooltip';
import { ProjectWidgetContainer } from '@frontend/app/containers/Projects/OverviewPage/Widgets/common/ProjectWidgetContainer';
import { formatFullNumber, formatNumberDefault } from '@frontend/applications/Social/utils/formatNumber';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';

export const engagementCardFragment = graphql(`
  fragment EngagementCard_TotalInsights on TotalInsights {
    engagements
    organicEngagements
    paidEngagements
  }
`);

interface EngagementCardProps {
  data: FragmentType<typeof engagementCardFragment> | null | undefined;
  previousPeriodData?: FragmentType<typeof engagementCardFragment> | null | undefined;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
  showComparisonTooltip?: boolean;
  isLoading?: boolean;
}

export const EngagementCard: React.FC<EngagementCardProps> = ({
  data,
  previousPeriodData,
  startDate,
  endDate,
  dateType,
  showComparisonTooltip = false,
  isLoading = false,
}) => {
  const totalInsights = useFragment(engagementCardFragment, data);
  const previousTotalInsights = useFragment(engagementCardFragment, previousPeriodData);

  const value = totalInsights?.engagements ?? 0;
  const previousValue = previousTotalInsights?.engagements ?? 0;
  const paidValue = totalInsights?.paidEngagements ?? 0;
  const organicValue = totalInsights?.organicEngagements ?? 0;

  const fullFormattedValue = formatFullNumber(value);
  const fullFormattedPaidValue = formatFullNumber(paidValue);
  const fullFormattedOrganicValue = formatFullNumber(organicValue);

  if (isLoading) {
    return (
      <ProjectWidgetContainer title="Engagement" icon={<HandsClappingIcon className="text-pink-5" />} tooltip="Total likes, comments, saves, and shares. for any available post type." className="w-full">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>
      </ProjectWidgetContainer>
    );
  }

  return (
    <TooltipProvider>
      <ProjectWidgetContainer
        title="Engagement"
        icon={<HandsClappingIcon className="text-pink-5" />}
        tooltip="Total likes, comments, saves, and shares. for any available post type."
        className="w-full"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <HighlightCardStat
              className="text-2xl leading-none"
              role="status"
              aria-label={`Total engagement count: ${formatNumberDefault(value)}`}
              data-dd-action-name="engagement-stat"
            >
              {formatNumberDefault(value)}
            </HighlightCardStat>
          </TooltipTrigger>
          <TooltipContent role="tooltip" aria-live="polite">
            <TooltipHeader>
              <TooltipTitle className="mb-0">Total Engagement</TooltipTitle>
              <TooltipItemValue className="mb-2 text-sm">{fullFormattedValue}</TooltipItemValue>
              <TooltipTitle className="mb-0">Paid Engagement</TooltipTitle>
              <TooltipItemValue className="mb-2 text-sm">{fullFormattedPaidValue}</TooltipItemValue>
              <TooltipTitle className="mb-0">Organic Engagement</TooltipTitle>
              <TooltipItemValue className="text-sm">{fullFormattedOrganicValue}</TooltipItemValue>
            </TooltipHeader>
            <TooltipArrow />
          </TooltipContent>
        </Tooltip>

        {previousPeriodData && (
          <MetricChangeIndicator
            current={value}
            previous={previousValue}
            startDate={startDate}
            endDate={endDate}
            dateType={dateType}
            showTimeRange={false}
            showComparisonTooltip={showComparisonTooltip}
          />
        )}
        <HighlightCardStatLabel className="flex items-center gap-2 mt-2">
          <TooltipItemValue>{fullFormattedPaidValue} Paid</TooltipItemValue>
          <Separator orientation="vertical" className="h-4" />
          <TooltipItemValue>{fullFormattedOrganicValue} Organic</TooltipItemValue>
        </HighlightCardStatLabel>
      </ProjectWidgetContainer>
    </TooltipProvider>
  );
};
